import React, { useState, useMemo, useCallback } from "react";
import { Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { type RiskRating } from "~/graphql/supergraphTypes";
import InspectRiskRatingModal from "../../../InspectRiskRatingModal";
import { getEffectiveRiskRating } from "../../utils/riskRatingCalculations";
import {
  successIcon,
  successText,
  dangerIcon,
  boltIcon,
  viewTextLabel,
} from "../styles_vanilla.css";
import { CHANGE_CONFIG, STATUS_CONFIG } from "./config";
import {
  getLinkedRisk,
  getInvestorRisk,
  getSimulatedRisk,
  calculateRiskRatingChange,
} from "./helpers";
import {
  changeIndicatorContainer,
  dash,
  icon,
  iconContainer,
  inspectIcon,
  inspectViewWrapper,
} from "./styles_vanilla.css";
import {
  RiskRatingStatusProps,
  ArrowIndicatorProps,
  DashIndicatorProps,
  ChangeIndicatorProps,
  RiskStatusType,
  RiskRatingData,
} from "./types";

// Constants
/**
 * Array of risk status types that support inspection functionality.
 *
 * These statuses will:
 * - Show the "Inspect" text and search icon on hover
 * - Open the risk rating inspection modal when clicked
 * - Provide detailed risk analysis information
 *
 * Includes all risk levels (Low, Medium, High) with various change types
 * but excludes special statuses like PENDING, MANUAL, NOT_DETERMINED, etc.
 */
const INSPECTABLE_STATUSES: RiskStatusType[] = [
  // High Risk Statuses
  RiskStatusType.HIGH_INCREASE,
  RiskStatusType.HIGH_DECREASE,
  RiskStatusType.HIGH_UNCHANGED,
  RiskStatusType.HIGH_NONE,
  RiskStatusType.HALT_NONE,

  // Medium Risk Statuses
  RiskStatusType.MEDIUM_UNCHANGED,
  RiskStatusType.MEDIUM_INCREASE,
  RiskStatusType.MEDIUM_DECREASE,
  RiskStatusType.MEDIUM_NONE,

  // Low Risk Statuses
  RiskStatusType.LOW_UNCHANGED,
  RiskStatusType.LOW_DECREASE_TWO,
  RiskStatusType.LOW_DECREASE,
  RiskStatusType.LOW_NONE,
];

// Helper functions
const getIconClassName = (status: RiskStatusType): string => {
  const isHighRiskOrIncrease = status.startsWith("high") || status.includes("increase");
  return isHighRiskOrIncrease ? `${dangerIcon} ${icon}` : `${successText} ${icon}`;
};

const getDashClassName = (status: RiskStatusType): string => {
  const isHighRisk = status.startsWith("high");
  return `${isHighRisk ? dangerIcon : successText} ${dash}`;
};

// Components
const DashIndicator: React.FC<DashIndicatorProps> = ({ status }) => (
  <div className={iconContainer}>
    <span className={getDashClassName(status)}>—</span>
  </div>
);

const ArrowIndicator: React.FC<ArrowIndicatorProps> = ({ config, status }) => {
  const iconClassName = getIconClassName(status);

  if (config.count === 2) {
    return (
      <>
        <div className={iconContainer}>
          <Icon type={config.type} className={iconClassName} data-testid={config.type} />
        </div>
        <div className={iconContainer}>
          <Icon type={config.type} className={iconClassName} data-testid={config.type} />
        </div>
      </>
    );
  }

  return (
    <div className={iconContainer}>
      <Icon type={config.type} className={iconClassName} data-testid={config.type} />
    </div>
  );
};

const ChangeIndicator: React.FC<ChangeIndicatorProps> = ({ status }) => {
  const changeConfig = CHANGE_CONFIG[status];
  if (!changeConfig) return null;

  if ("showManual" in changeConfig || "showDash" in changeConfig) {
    return <DashIndicator status={status} />;
  }

  return <ArrowIndicator config={changeConfig} status={status} />;
};

const InspectView: React.FC = () => (
  <Flex className={inspectViewWrapper}>
    <Icon type="search" className={inspectIcon} data-testid="search" />
    <Text className={viewTextLabel}>Inspect</Text>
  </Flex>
);

/**
 * RiskRatingStatus component displays risk rating with change indicators and inspection capabilities
 *
 * Features:
 * - Displays risk level (Low, Medium, High) with appropriate icons
 * - Shows change indicators (arrows, dashes) based on risk changes
 * - Provides inspection modal for detailed risk analysis
 * - Supports hover interactions for better UX
 *
 * @example
 * // For low risk with decrease
 * <RiskRatingStatus status={RiskStatusType.LOW_DECREASE} />
 *
 * // For low risk with double decrease
 * <RiskRatingStatus status={RiskStatusType.LOW_DECREASE_TWO} />
 *
 * // For medium risk with increase
 * <RiskRatingStatus status={RiskStatusType.MEDIUM_INCREASE} />
 *
 * // For any risk with no change
 * <RiskRatingStatus status={RiskStatusType.LOW_UNCHANGED} />
 *
 * // For high risk with no change (inspectable)
 * <RiskRatingStatus status={RiskStatusType.HIGH_NONE} />
 */
export const RiskRatingStatus: React.FC<RiskRatingStatusProps> = ({
  status,
  data,
  picrRevampEnabled = false,
  newData,
  currentRiskRating,
  newRiskRating,
  positionName,
}): JSX.Element => {
  const [showInspectModal, setShowInspectModal] = useState(false);

  const statusConfig = STATUS_CONFIG[status];
  const showBoltIcon = status === RiskStatusType.MEDIUM_MANUAL;
  const showInspectView = INSPECTABLE_STATUSES.includes(status);

  const handleClick = useCallback(() => {
    if (showInspectView) {
      setShowInspectModal(true);
    }
  }, [showInspectView]);

  const handleCloseModal = useCallback(() => {
    setShowInspectModal(false);
  }, []);

  const investorRiskRating = newData?.linkedOnboardingCase?.investingProfile?.riskRating;

  const existingBankAccountRiskRating = getEffectiveRiskRating(
    newData?.linkedOnboardingCase?.paymentAccount?.riskRating ?? undefined,
  );
  const proposedBankAccountRiskRating = getEffectiveRiskRating(
    newData?.currentPaymentAccount?.riskRating ?? undefined,
  );

  const existingBeneficiaryRiskRating = getEffectiveRiskRating(
    newData?.existingBeneficiaryRiskRating ?? undefined,
  );
  const proposedBeneficiaryRiskRating = getEffectiveRiskRating(
    newData?.newBeneficiaryRiskRating ?? undefined,
  );

  const riskRatingRowData: RiskRatingData[] = useMemo(
    () => [
      {
        name: "Bank account domicile",
        existing: {
          country: newData?.linkedOnboardingCase?.paymentAccount?.bankCountry?.name ?? "",
          risk: picrRevampEnabled ? (existingBankAccountRiskRating ?? null) : null,
        },
        proposed: {
          country: newData?.currentPaymentAccount?.bankCountry?.name ?? "",
          risk: picrRevampEnabled ? (proposedBankAccountRiskRating ?? null) : null,
          changeIndicator: picrRevampEnabled
            ? calculateRiskRatingChange(
                existingBankAccountRiskRating,
                proposedBankAccountRiskRating,
              )
            : undefined,
        },
      },
      {
        name: picrRevampEnabled ? "Payment beneficiary" : "Payment beneficiary risk rating",
        existing: {
          country: "",
          risk: picrRevampEnabled
            ? getEffectiveRiskRating(newData?.existingBeneficiaryRiskRating ?? undefined)
            : getLinkedRisk(data),
        },
        proposed: {
          country: "",
          risk: picrRevampEnabled
            ? getEffectiveRiskRating(newData?.newBeneficiaryRiskRating ?? undefined)
            : getSimulatedRisk("paymentBeneficiaryRiskRating", data),
          changeIndicator: picrRevampEnabled
            ? calculateRiskRatingChange(
                existingBeneficiaryRiskRating,
                proposedBeneficiaryRiskRating,
              )
            : undefined,
        },
      },
      {
        name: picrRevampEnabled ? "Investor" : "Investor risk rating",
        existing: {
          country: "",
          risk: picrRevampEnabled
            ? getEffectiveRiskRating(investorRiskRating)
            : getInvestorRisk(data),
        },
        proposed: {
          country: "",
          risk: picrRevampEnabled
            ? getEffectiveRiskRating(investorRiskRating)
            : getSimulatedRisk("investorProfileRiskRating", data),
          changeIndicator: picrRevampEnabled ? "dash" : undefined,
        },
      },
      {
        name: picrRevampEnabled ? "Position" : "Position risk rating",
        existing: {
          country: "",
          risk: picrRevampEnabled ? (currentRiskRating as RiskRating) : getInvestorRisk(data),
        },
        proposed: {
          country: "",
          risk: picrRevampEnabled
            ? (newRiskRating as RiskRating)
            : getSimulatedRisk("investorProfileRiskRating", data),
          changeIndicator: picrRevampEnabled
            ? calculateRiskRatingChange(
                currentRiskRating as RiskRating,
                newRiskRating as RiskRating,
              )
            : undefined,
        },
      },
    ],
    [
      currentRiskRating,
      data,
      existingBankAccountRiskRating,
      existingBeneficiaryRiskRating,
      investorRiskRating,
      newData?.currentPaymentAccount?.bankCountry?.name,
      newData?.existingBeneficiaryRiskRating,
      newData?.linkedOnboardingCase?.paymentAccount?.bankCountry?.name,
      newData?.newBeneficiaryRiskRating,
      newRiskRating,
      picrRevampEnabled,
      proposedBankAccountRiskRating,
      proposedBeneficiaryRiskRating,
    ],
  );

  const paymentBeneficiariesCount = useMemo(() => {
    // Get profiles from linkedOnboardingCase
    const profiles =
      ((data?.linkedOnboardingCase as any)?.profiles as Array<{
        parentRelationships?: Array<{
          relationshipType: string;
        }>;
      }>) || [];

    // Filter for payment beneficiaries
    const paymentBeneficiaries = profiles.filter(
      (profile) =>
        profile.parentRelationships?.some(
          (relationship) =>
            relationship.relationshipType.toLowerCase() === "payment_beneficiary",
        ) ?? false,
    );

    // Check if investing profile has payment accounts
    const investingProfile = data?.linkedOnboardingCase?.investingProfile;
    const investingProfilePaymentsCount =
      investingProfile && "paymentAccounts" in investingProfile
        ? investingProfile.paymentAccounts?.length || 0
        : 0;

    // Total count is payment beneficiaries plus investing profile payments (if any)
    return paymentBeneficiaries.length + (investingProfilePaymentsCount > 0 ? 1 : 0);
  }, [data]);

  return (
    <>
      <Flex
        className={statusConfig.containerClass}
        data-testid="risk-status-container"
        onClick={handleClick}
      >
        {showBoltIcon && (
          <Icon type="bolt" variant="solid" className={boltIcon} data-testid="bolt" />
        )}

        {statusConfig.icon && (
          <Icon
            type={statusConfig.icon.type}
            variant="outline"
            className={showBoltIcon ? successIcon : statusConfig.icon.className}
            data-testid={statusConfig.icon.type}
          />
        )}

        <Text style={{ width: "100%" }} className={statusConfig.text.className}>
          {statusConfig.text.content}
        </Text>

        {showInspectView && <InspectView />}

        <Flex className={changeIndicatorContainer}>
          <ChangeIndicator status={status} />
        </Flex>
      </Flex>

      <InspectRiskRatingModal
        showModal={showInspectModal}
        onClose={handleCloseModal}
        riskRatingRowData={riskRatingRowData}
        paymentBeneficiariesCount={paymentBeneficiariesCount}
        picrRevampEnabled={picrRevampEnabled}
        positionName={positionName ?? ""}
      />
    </>
  );
};
